import {
  ArrowUpRight,
  Bookmark,
  BookmarkCheck,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Loader2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import PracticeJobSpecificModal from "../../practice/jobSpecific/PracticeJobSpecificModal";
import { Tooltip } from "antd";
import { bookmarkQuestionApi } from "../../../../api/candidate";
import WebUtils from "../../../../utils/WebUtils";

const getDifficultyColor = (difficulty) => {
  switch (difficulty.toLowerCase()) {
    case "easy":
      return "bg-green-100 text-green-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "hard":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const QuestionCard = ({
  question,
  jobGuideIdentifier,
  userDetail,
  industryId,
  setLoading,
}) => {
  const [openJobSpecificModal, setOpenJobSpecificModal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [isBookMarked, setIsBookmarked] = useState(question?.bookmarked);
  const [expandedSample, setExpandedSample] = useState([]);

  // useEffect(() => {
  //   if (question?.bookmarked) {
  //     setIsBookmarked(true);
  //   } else {
  //     setIsBookmarked(false);
  //   }
  // }, [question]);

  const toggleSampleAnswer = (index) => {
    setExpandedSample((prev) => ({
      ...prev,
      [index]: !prev[index], // toggle true/false
    }));
  };

  const handleHint = () => {
    setShowHint((prev) => !prev);
  };

  const handleBookmark = (bookmark) => {
    let url = `${bookmarkQuestionApi}?jobGuideIdentifier=${jobGuideIdentifier}&userIdentifier=${
      userDetail?.userIdentifier || null
    }&questionId=${question?.questionId}&isBookMarked=${bookmark}`;

    setBookmarkLoading(true);
    WebUtils.httpOperations(url, {}, "POST")
      .then((result) => {
        setBookmarkLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          if (bookmark) {
            setIsBookmarked(true);
          } else {
            setIsBookmarked(false);
          }
        }
      })
      .catch((err) => {
        setBookmarkLoading(false);
        console.error(err);
      });
  };

  return (
    <>
      {openJobSpecificModal && (
        <PracticeJobSpecificModal
          question={question}
          guideId={jobGuideIdentifier}
          userDetail={userDetail}
          openJobSpecificModal={openJobSpecificModal}
          setOpenJobSpecificModal={setOpenJobSpecificModal}
          industryId={industryId}
          selectedQuestion={question}
          questionBank={true}
        />
      )}
      <div className="border border-gray-300 shadow-sm bg-white px-[1.15rem] py-[.95rem] rounded-lg">
        <div className="flex flex-wrap gap-2 justify-between items-start">
          <div className="flex-1 text-[.95rem] leading-[1.4rem] font-semibold text-[#101828]">
            {question?.questionTitle}
          </div>

          <div className="flex gap-2 items-center">
            <span className="text-[.75rem] text-gray-600">
              {question?.difficulty && (
                <span
                  className={`px-2 py-[.15rem] border border-gray-200 shadow-sm font-medium rounded-lg ${getDifficultyColor(
                    question?.difficulty
                  )}`}
                >
                  {question?.difficulty
                    ? question.difficulty.charAt(0).toUpperCase() +
                      question.difficulty.slice(1)
                    : ""}
                </span>
              )}
            </span>

            <div className="w-[21px]">
              {!bookmarkLoading ? (
                isBookMarked ? (
                  <Tooltip title="Remove from bookmarks">
                    <div
                      className="cursor-pointer"
                      onClick={() => handleBookmark(false)}
                    >
                      <BookmarkCheck size={21} color="currentColor" />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title="Add to bookmarks">
                    <div
                      className="cursor-pointer"
                      onClick={() => handleBookmark(true)}
                    >
                      <Bookmark size={20} color="currentColor" />
                    </div>
                  </Tooltip>
                )
              ) : (
                <Loader2 size={20} color="#ED7422" className="animate-spin" />
              )}
            </div>
          </div>
        </div>
        <div>
          {showHint && (
            <div className="mt-[.6rem] max-h-[50vh] px-6  py-[.75rem] bg-gray-200 rounded-lg border border-gray-100 overflow-y-auto orange-scroll">
              <div className="">
                {/* Question Explanation */}
                <div className="mb-3">
                  <div className="text-[.96rem] font-semibold text-black mb-2">
                    Question Explanation
                  </div>
                  <p className="text-gray-600 font-medium rounded-lg p-5 leading-relaxed bg-white">
                    {question?.explanation
                      ? question?.explanation
                      : "No question explanation found!"}
                  </p>
                </div>

                {/* Approach to Answer */}
                {question?.approachToAnswer && (
                  <div className="mb-2">
                    <div className="text-[.96rem] font-semibold text-black mb-2">
                      Approach to Answer
                    </div>
                    <p className="text-gray-600 font-medium rounded-lg p-5 leading-relaxed bg-white">
                      {question?.approachToAnswer
                        ? question?.approachToAnswer
                        : "No Answer Approach found!"}
                    </p>
                  </div>
                )}
              </div>
              {/* Sample Answers */}
              <div className="mt-3">
                {question?.sampleAnswerList &&
                question.sampleAnswerList.length > 0 ? (
                  <div>
                    <div className="text-[.96rem] font-semibold text-black mb-2">
                      Sample Answers
                    </div>
                    <div className="space-y-2">
                      {question.sampleAnswerList.map((sampleAnswer, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg bg-white"
                        >
                          <button
                            onClick={() => toggleSampleAnswer(index)}
                            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                          >
                            <span className="font-semibold text-gray-800">
                              Sample Answer {index + 1}
                            </span>
                            {expandedSample[index] === true ? (
                              <ChevronDown
                                size={20}
                                className="text-gray-500"
                              />
                            ) : (
                              <ChevronRight
                                size={20}
                                className="text-gray-500"
                              />
                            )}
                          </button>

                          {expandedSample[index] === true && (
                            <div className="px-4 pb-3  border-gray-200">
                              <div className="">
                                <p className="text-gray-700 font-medium leading-relaxed text-sm">
                                  {sampleAnswer}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>No Sample Answer found!</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="mt-[.8rem] text-[.88rem] flex gap-2 flex-wrap items-center justify-between">
          <button
            onClick={handleHint}
            className="flex text-[.88rem] font-semibold gap-[.35rem] justify-center items-center text-[#475467] hover:text-amber-500 transition-all duration-200"
          >
            {showHint ? (
              <>
                <ChevronUp size={20} color="currentColor" /> Hide Answer
              </>
            ) : (
              <>
                <ChevronDown size={20} color="currentColor" /> Show Answer
              </>
            )}
          </button>
          <div
            onClick={() => setOpenJobSpecificModal(true)}
            className="text-[#ED7422] font-semibold flex gap-[.4rem] cursor-pointer items-center hover:brightness-[1.3] transition-all duration-200"
          >
            Practice Now
            {/* <ArrowUpRight size={20} color="currentColor" /> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionCard;
